import React, { useState, useEffect, useCallback, useRef } from 'react';
import { spanishFor } from '@/content/es';
import { Link, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Menu, Moon, Sun, X, ChevronDown } from 'lucide-react';
import { useTheme } from '@/hooks/use-theme';
import { AnimatePresence, motion } from 'framer-motion';

import { CALENDLY_URL } from '@/lib/site';

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [visible, setVisible] = useState(true);
  const [offersDropdown, setOffersDropdown] = useState(false);
  const whatsappUrl = 'https://wa.me/12026998621';
  const location = useLocation();
  const { theme, setTheme } = useTheme();

  // The bar's state is read straight from window.scrollY on every scroll,
  // on mount and on route change, instead of only 10 ms after a scroll event.
  // The bar hides while scrolling down and returns on scroll up. A jump away
  // from the very top (browser scroll restoration after ScrollToTop) is taken
  // as the new baseline rather than a downward scroll, so it doesn't hide the bar.
  const lastScrollY = useRef(0);
  const syncWithScroll = useCallback(() => {
    const y = window.scrollY;
    const scrollingDown = lastScrollY.current > 0 && y > lastScrollY.current;
    setVisible(y < 150 || !scrollingDown);
    setScrolled(y > 20);
    lastScrollY.current = y;
  }, []);

  useEffect(() => {
    syncWithScroll();
    window.addEventListener('scroll', syncWithScroll, { passive: true });
    return () => window.removeEventListener('scroll', syncWithScroll);
  }, [syncWithScroll]);

  // Route changes reset the scroll position; re-read it rather than wait for an event.
  useEffect(() => {
    syncWithScroll();
  }, [location.pathname, syncWithScroll]);

  useEffect(() => {
    setIsOpen(false);
    setOffersDropdown(false);
  }, [location]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'About', path: '/about' },
    { name: 'Services', path: '/services' },
    { name: 'Products', path: '/products' },
    { name: 'Portfolio', path: '/portfolio' },
    { name: 'Reviews', path: '/testimonials' },
    { name: 'Contact', path: '/contact' },
  ];

  const currentOffers = [
    { name: 'Affordable Website Design', path: '/affordable-website-design', description: 'Under $99 for 2 pages' },
    { name: 'Google Ads Management', path: '/google-ads-management', description: '$300/month flat fee' },
  ];

  // Only reviewed Spanish pages are offered; drafts are never linked.
  const esPath = spanishFor(location.pathname);
  const langLink = esPath ? (
    <a href={esPath} hrefLang="es" lang="es" className="px-2 py-2 text-sm font-medium text-slate-700 dark:text-slate-200 hover:text-primary" aria-label="Ver esta página en español">
      Español
    </a>
  ) : null;

  const toggleTheme = () => {
    setTheme(theme === 'dark' ? 'light' : 'dark');
  };

  return (
    <motion.nav
      initial={{ y: 0 }}
      animate={{
        y: visible ? 0 : -100,
        opacity: visible ? 1 : 0
      }}
      transition={{ duration: 0.3 }}
      className={`fixed w-full z-50 transition-all duration-300 ${
        // Solid, not translucent: at 90% opacity the page text showed through
        // the bar on mobile. The background is also solid while the mobile
        // menu is open; the height only follows scrolling, so opening the
        // menu at the top doesn't make the bar jump.
        `${scrolled ? 'py-2 shadow-md' : 'py-4'} ${scrolled || isOpen ? 'bg-white dark:bg-slate-900' : 'bg-transparent'}`
      }`}
    >
      <div className="container-custom">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <Link
            to="/"
            className="flex items-center gap-2 relative z-20"
            aria-label="OmniCraft Home"
          >
            {/* logo.png is a stacked lockup (circular mark above the wordmark).
                object-top crops to just the mark, which fills the top square of
                the source; centring it cut the mark and bled in the wordmark.
                The wordmark is rendered as text alongside. */}
            <img
              src="/logo.png"
              alt="OmniCraft logo"
              className="h-9 w-9 rounded-full object-cover object-top md:h-10 md:w-10"
              loading="eager"
            />
            <span className="text-lg md:text-xl font-bold text-primary whitespace-nowrap">
              Omni<span className="text-omni-navy dark:text-white">Craft</span>
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center space-x-1">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`px-3 py-2 rounded-md text-sm font-medium transition-colors relative group ${
                  location.pathname === link.path
                    ? 'text-primary'
                    : 'text-slate-700 dark:text-slate-200 hover:text-primary dark:hover:text-primary'
                }`}
              >
                {link.name}
                {location.pathname === link.path && (
                  <motion.span
                    layoutId="navbar-indicator"
                    className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary rounded-full"
                    transition={{ type: 'spring', duration: 0.6 }}
                  />
                )}
              </Link>
            ))}

            {/* Current Offers Dropdown */}
            <div className="relative">
              <button
                onClick={() => setOffersDropdown(!offersDropdown)}
                className="px-3 py-2 rounded-md text-sm font-medium transition-colors text-slate-700 dark:text-slate-200 hover:text-primary dark:hover:text-primary flex items-center gap-1"
              >
                Current Offers
                <ChevronDown className={`h-4 w-4 transition-transform ${offersDropdown ? 'rotate-180' : ''}`} />
              </button>

              <AnimatePresence>
                {offersDropdown && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    transition={{ duration: 0.2 }}
                    className="absolute top-full left-0 mt-1 w-64 bg-white dark:bg-slate-800 rounded-lg shadow-xl border border-slate-200 dark:border-slate-700 overflow-hidden z-50"
                    onMouseLeave={() => setOffersDropdown(false)}
                  >
                    {currentOffers.map((offer, idx) => (
                      <Link
                        key={offer.path}
                        to={offer.path}
                        onClick={() => setOffersDropdown(false)}
                        className={`block px-4 py-3 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors ${idx < currentOffers.length - 1 ? 'border-b border-slate-200 dark:border-slate-700' : ''}`}
                      >
                        <div className="font-medium text-slate-900 dark:text-white">{offer.name}</div>
                        <div className="text-xs text-slate-600 dark:text-slate-400">{offer.description}</div>
                      </Link>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>

          {/* Actions */}
          <div className="hidden lg:flex items-center space-x-3">
            {langLink}
            <a
              href={whatsappUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="rounded-full border border-primary/40 bg-primary/10 px-3 py-2 text-sm font-medium text-primary transition-colors hover:bg-primary/20"
              aria-label="Message us on WhatsApp"
            >
              WhatsApp
            </a>
            <Button
              variant="ghost"
              size="icon"
              onClick={toggleTheme}
              className="rounded-full"
              aria-label={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
            >
              {theme === 'dark' ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
            </Button>

            <Button
              className="rounded-full"
              asChild
            >
              <a href={CALENDLY_URL} target="_blank" rel="noopener noreferrer">
                Talk to Us
              </a>
            </Button>
          </div>

          {/* Mobile menu button & theme toggle */}
          <div className="flex items-center space-x-1 lg:hidden relative z-50">
            {langLink}
            <Button
              variant="ghost"
              size="icon"
              onClick={toggleTheme}
              className="rounded-full"
              aria-label={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
            >
              {theme === 'dark' ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
            </Button>

            <Button
              variant="ghost"
              size="icon"
              onClick={() => setIsOpen(!isOpen)}
              aria-label={isOpen ? 'Close menu' : 'Open menu'}
              className="relative"
            >
              <AnimatePresence mode="wait">
                <motion.div
                  key={isOpen ? 'close' : 'menu'}
                  initial={{ opacity: 0, rotate: isOpen ? -90 : 90 }}
                  animate={{ opacity: 1, rotate: 0 }}
                  exit={{ opacity: 0, rotate: isOpen ? 90 : -90 }}
                  transition={{ duration: 0.2 }}
                >
                  {isOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
                </motion.div>
              </AnimatePresence>
            </Button>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, x: '100%' }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: '100%' }}
            transition={{ duration: 0.3, ease: 'easeInOut' }}
            className="lg:hidden fixed inset-0 top-16 z-40 bg-white dark:bg-slate-900 overflow-auto"
          >
            <div className="container py-8">
              <div className="flex flex-col space-y-2">
                {navLinks.map((link, index) => (
                  <motion.div
                    key={link.path}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05 }}
                  >
                    <Link
                      to={link.path}
                      className={`px-4 py-3 text-lg font-medium rounded-md flex items-center justify-between ${
                        location.pathname === link.path
                          ? 'bg-primary/10 text-primary'
                          : 'text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800'
                      }`}
                    >
                      {link.name}
                      <ChevronRight className="h-4 w-4 opacity-50" />
                    </Link>
                  </motion.div>
                ))}

                {/* Mobile Current Offers */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: navLinks.length * 0.05 }}
                  className="py-2"
                >
                  <button
                    onClick={() => setOffersDropdown(!offersDropdown)}
                    className="w-full px-4 py-3 text-lg font-medium rounded-md flex items-center justify-between text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800"
                  >
                    Current Offers
                    <ChevronRight className={`h-4 w-4 opacity-50 transition-transform ${offersDropdown ? 'rotate-90' : ''}`} />
                  </button>

                  <AnimatePresence>
                    {offersDropdown && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        className="overflow-hidden"
                      >
                        {currentOffers.map((offer) => (
                          <Link
                            key={offer.path}
                            to={offer.path}
                            onClick={() => {
                              setOffersDropdown(false);
                              setIsOpen(false);
                            }}
                            className="block px-8 py-2.5 text-slate-700 dark:text-slate-200 hover:text-primary dark:hover:text-primary border-l-2 border-primary/30 hover:border-primary transition-colors"
                          >
                            <div className="font-medium">{offer.name}</div>
                            <div className="text-xs text-slate-600 dark:text-slate-400">{offer.description}</div>
                          </Link>
                        ))}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: (navLinks.length + 1) * 0.05 }}
                  className="pt-4 space-y-3"
                >
                  <Button
                    variant="outline"
                    className="w-full justify-center py-6 text-lg"
                    asChild
                  >
                    <a href={whatsappUrl} target="_blank" rel="noopener noreferrer">
                      Message us on WhatsApp
                    </a>
                  </Button>
                  <Button
                    className="w-full justify-center py-6 text-lg"
                    asChild
                  >
                    <a href={CALENDLY_URL} target="_blank" rel="noopener noreferrer">
                      Talk to Us
                    </a>
                  </Button>
                </motion.div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.nav>
  );
};

export default React.memo(Navbar);

const ChevronRight = (props) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    {...props}
  >
    <path d="m9 18 6-6-6-6"/>
  </svg>
);
